import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { benutzerSchluessel } from '../api';

/**
 * Die eigene Adresse für die Verwaltung freigeben.
 *
 * ══ WARUM OHNE EINGABEFELD ═════════════════════════════════════════════════
 *
 * Freigegeben wird genau die Adresse, von der die Anfrage kommt — sie steht im
 * Request und wird nicht übergeben. Ein Feld, in das man etwas hineinschreiben
 * kann, hiesse: jeder Angemeldete darf eine beliebige Adresse durch die
 * Firewall lassen, auch eine fremde. Das ist eine andere Art von Recht als
 * „ich komme von hier gerade nicht an die Verwaltung".
 *
 * Freie Eingabe — fremde Adressen, ganze Netze — bleibt den Admins in der
 * Benutzerverwaltung.
 *
 * ══ WAS FREIGEGEBEN WIRD ═══════════════════════════════════════════════════
 *
 * Nicht der PC — der ist ohnehin erreichbar, sonst wäre man hier nicht
 * angemeldet. Es geht um die Verwaltungsoberflächen dahinter: die Firewall des
 * Anbieters vor dem Verwaltungswirt und die Zugangsliste im Proxy. Seit
 * 1.0.167 ausserdem SSH auf unsere Server (ufw, nur der SSH-Port) — dafür
 * steht der Knopf in diesem Dialog.
 */
export default function MeineAdressFreigabe() {
  const qc = useQueryClient();
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);

  const lage = useQuery({
    queryKey: ['meine-adresse'],
    queryFn: benutzerSchluessel.meineAdresse,
  });

  const freigeben = useMutation({
    mutationFn: benutzerSchluessel.meineAdresseFreigeben,
    onSuccess: (e) => {
      setMeldung({
        ok: e.ok,
        text: e.ok
          ? `${e.adresse} freigegeben.${e.hinweis ? ` ${e.hinweis}` : ''}`
          : `Teilweise: ${e.schritte
              .map((s) => `${s.name}: ${s.meldung ?? '?'}`)
              .join(' · ')}`,
      });
      qc.invalidateQueries({ queryKey: ['meine-adresse'] });
      qc.invalidateQueries({ queryKey: ['benutzer-schluessel'] });
    },
    onError: (e: Error) => setMeldung({ ok: false, text: e.message }),
  });

  const zurueck = useMutation({
    mutationFn: (id: string) => benutzerSchluessel.meineFreigabeEntfernen(id),
    onSuccess: () => {
      setMeldung({ ok: true, text: 'Freigabe zurückgezogen.' });
      qc.invalidateQueries({ queryKey: ['meine-adresse'] });
      qc.invalidateQueries({ queryKey: ['benutzer-schluessel'] });
    },
  });

  const l = lage.data;

  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <Globe size={14} /> Meine Adresse
      </div>

      {lage.isLoading ? (
        <p className="mt-1 text-[11px] text-slate-400">wird ermittelt …</p>
      ) : !l?.adresse ? (
        <p className="mt-1 text-[11px] text-amber-700">
          Die eigene Adresse liess sich nicht bestimmen. Dann muss ein Admin sie eintragen.
        </p>
      ) : (
        <>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <code className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-800">
              {l.adresse}
            </code>
            {l.freigegeben ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                <CheckCircle2 size={11} /> freigegeben
              </span>
            ) : (
              <span className="text-[11px] text-slate-500">nicht freigegeben</span>
            )}

            {l.freigegeben && l.eintrag ? (
              <button
                type="button"
                onClick={() => zurueck.mutate(l.eintrag!.id)}
                disabled={zurueck.isPending}
                title="Freigabe zurückziehen"
                className="ml-auto inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                <Trash2 size={10} /> zurückziehen
              </button>
            ) : (
              <button
                type="button"
                onClick={() => freigeben.mutate()}
                disabled={freigeben.isPending}
                className="ml-auto rounded bg-brand-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
              >
                {freigeben.isPending ? 'trägt ein …' : 'freigeben'}
              </button>
            )}
          </div>

          {/* Wohin die Freigabe wirkt.
              Seit es Proxys auf mehreren Wirten gibt, ist "freigegeben" keine
              vollstaendige Auskunft mehr: Eine Adresse kann an coruscants
              Oberflaechen ankommen und an tatooines nicht. Ohne diese Zeile
              sieht beides gleich aus -- und wer dann vor einem 401 sitzt,
              sucht den Fehler bei sich. */}
          {l.ziele && l.ziele.length > 1 && (
            <p className="mt-1.5 text-[10px] text-slate-500">
              Gilt für:{' '}
              {l.ziele.map((z, i) => (
                <span key={z.url}>
                  {i > 0 && ' · '}
                  <span className={z.eingerichtet ? '' : 'text-amber-700'} title={z.url}>
                    {z.name}
                    {!z.eingerichtet && ' (nicht eingerichtet)'}
                  </span>
                </span>
              ))}
            </p>
          )}

          {l.adresse.includes(':') && !l.freigegeben && (
            <p className="mt-1.5 flex items-start gap-1 text-[10px] text-amber-700">
              <AlertTriangle size={10} className="mt-px shrink-0" />
              Das ist eine IPv6-Adresse. Trifft die Firewall des Anbieters nur IPv4, wirkt der
              Eintrag dort nicht.
            </p>
          )}
        </>
      )}

      {meldung && (
        <p className={`mt-1.5 text-[11px] ${meldung.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
          {meldung.text}
        </p>
      )}

      <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">
        Gemeint ist nicht der PC — der ist ohnehin erreichbar, sonst wärst du hier nicht
        angemeldet. Es geht um die Verwaltungsoberflächen dahinter. Freigegeben wird genau diese
        eine Adresse; für ein ganzes Netz oder eine fremde Adresse fragst du einen Admin.
      </p>
    </div>
  );
}
